import React from 'react';
import SteerChart from './SteerChart';
import {
  bigText, unitWords, goalText, progressToGoal, trendTone, progressWords,
  ringTone, arrowDir, sentenceFor, inBand,
} from './steerText';

// One number you're steering (You page, 2026-10 restyle). Reads top to bottom:
//   ring (how far along)  ·  which number  ·  the big number with an arrow
//   ·  the goal and how far  ·  one sentence  ·  the chart.
// The whole card is one tap: it opens the goal editor underneath.

const Ring = ({ pct, tone }) => {
  const c = 2 * Math.PI * 20;
  const on = Math.max(0, Math.min(100, pct ?? 0)) / 100 * c;
  const stroke = tone === 'g' ? 'stroke-good-strong dark:stroke-good' : tone === 'r' ? 'stroke-off-strong dark:stroke-off' : 'stroke-fg';
  return (
    <span className="absolute right-[18px] top-4 h-12 w-12" aria-hidden="true">
      <svg viewBox="0 0 48 48" className="block h-12 w-12 -rotate-90">
        <circle cx="24" cy="24" r="20" fill="none" strokeWidth="6" className="stroke-line" />
        {on > 0 && (
          <circle cx="24" cy="24" r="20" fill="none" strokeWidth="6" strokeLinecap="round"
            className={stroke} strokeDasharray={`${on} ${c + 1}`} />
        )}
      </svg>
    </span>
  );
};

const Arrow = ({ dir, tone }) => {
  if (!dir) return null;
  const cls = tone === 'good' ? 'text-good-strong dark:text-good' : tone === 'bad' ? 'text-off-strong dark:text-off' : 'text-fg-3';
  return <span className={`${cls} text-[1.6rem] font-normal`} aria-hidden="true">{dir === 'up' ? '↑' : '↓'}</span>;
};

const SteerCard = ({ card, metric, open, onOpen }) => {
  const there = inBand(card.now, card);
  const pct = progressToGoal(card);
  const tone = trendTone(card, metric);
  const words = progressWords(pct, tone, there);
  const goal = goalText(card);
  const big = bigText(card.now, card.unit);
  const hasChart = metric?.enough_data && (metric.history || []).length > 1;
  // The arrow's colour says whether the change is the right way for this goal.
  const dir = arrowDir(card, metric);
  const dirTone = tone || (dir
    ? ((card.direction === 'up') === (dir === 'up') || (card.direction === 'range' && there) ? 'good' : 'bad')
    : null);

  return (
    <button
      type="button"
      onClick={() => onOpen?.(card.metric_key)}
      aria-expanded={!!open}
      aria-label={`${card.label}. ${big ? `${big} ${unitWords(card.unit, card.metric_key)}.` : ''} ${goal ? `Goal ${goal}.` : ''} Tap to change the goal.`}
      className={`relative flex w-full min-w-0 flex-col text-left rounded-xl border bg-raised px-5 pb-4 pt-[18px] transition-colors hover:border-fg-3 hover:bg-elev focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fg-3 ${open ? 'border-fg-3' : 'border-line'}`}
    >
      {card.now != null && <Ring pct={pct} tone={ringTone(pct, tone, there)} />}

      <span className="pr-[60px] text-[1.15rem] font-medium text-fg">{card.label}</span>

      {big ? (
        <span className="mt-1.5 flex items-baseline gap-2 text-[2.6rem] font-medium leading-none tracking-[-0.03em] tabular-nums">
          <Arrow dir={dir} tone={dirTone} />
          {big}
          {unitWords(card.unit, card.metric_key) && (
            <small className="text-base font-normal tracking-normal text-fg-3">{unitWords(card.unit, card.metric_key)}</small>
          )}
        </span>
      ) : (
        <span className="mt-1.5 text-[2rem] font-medium leading-none tracking-[-0.02em] text-fg-3">No data yet</span>
      )}

      {goal && (
        <span className="mt-1 inline-flex items-center gap-2 text-sm text-fg-3">
          Goal <b className="font-medium text-fg-2">{goal}</b>{words && <> · {words}</>}
        </span>
      )}

      <span className="mt-1 text-[1.15rem] font-medium leading-[1.3] tracking-[-0.01em]">
        {sentenceFor(card, metric)}
      </span>

      {hasChart && <SteerChart card={card} metric={metric} />}
    </button>
  );
};

export default SteerCard;
